import React from 'react';
import { SortableTableClient } from './SortableTableClient';
import type { CellData } from './SortableTableClient';
import { parseTruthy } from '../utils';

/**
 * Recursively extracts plain text content from a React node tree.
 * Used for column headers and as the sortable/searchable text of each cell.
 */
function extractText(node: React.ReactNode): string {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node))
    return extractText(node.props.children);
  return '';
}

/**
 * Returns true when a React node tree contains only primitive text —
 * no element wrappers like `<a>`, `<strong>`, etc.
 */
function isPlainText(node: React.ReactNode): boolean {
  if (node == null) return true;
  if (typeof node === 'string' || typeof node === 'number') return true;
  if (Array.isArray(node)) return node.every(isPlainText);
  return false;
}

interface ShortcodeProps {
  shortcode_name?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * Walks the shortcode children (server-side) to collect column headers and
 * row data. This must run in a server component so the child elements are
 * still inspectable React elements — not pre-rendered nulls.
 *
 * Each cell carries both a plain `text` value (for sorting and search) and
 * the original `content` ReactNode (for display, preserving inline HTML
 * like `<a>`, `<strong>`, etc.).
 */
function parseShortcodeChildren(children: React.ReactNode): {
  headers: string[];
  rows: CellData[][];
} {
  const headers: string[] = [];
  const rows: CellData[][] = [];

  const flatChildren = React.Children.toArray(children);

  for (const child of flatChildren) {
    if (!React.isValidElement<ShortcodeProps>(child)) continue;

    const name = child.props.shortcode_name;

    if (name === 'hen-ui-sortable-table-head') {
      headers.push(extractText(child.props.children).trim());
    } else if (name === 'hen-ui-sortable-table-row') {
      const cells: CellData[] = [];
      const rowChildren = React.Children.toArray(child.props.children);
      for (const cell of rowChildren) {
        if (!React.isValidElement<ShortcodeProps>(cell)) continue;
        const cellName = cell.props.shortcode_name;
        if (cellName === 'hen-ui-sortable-table-cell') {
          const raw = cell.props.children;
          const text = extractText(raw).trim();
          cells.push({
            text,
            content: isPlainText(raw) ? text : raw,
          });
        }
      }
      if (cells.length > 0) rows.push(cells);
    }
  }

  return { headers, rows };
}

export interface SortableTableAdapterProps {
  title?: string;
  'items-per-page'?: string;
  'has-filter'?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * Server-side adapter that extracts structured data from shortcode children
 * and passes it to the interactive client DataTable component.
 */
export function SortableTableAdapter({
  title,
  'items-per-page': itemsPerPage,
  'has-filter': hasFilter,
  children,
}: SortableTableAdapterProps) {
  const { headers, rows } = parseShortcodeChildren(children);

  const parsed = itemsPerPage ? parseInt(itemsPerPage, 10) : NaN;
  const pageSize = Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
  const showSearch = parseTruthy(hasFilter);

  return (
    <SortableTableClient
      title={title}
      headers={headers}
      rows={rows}
      pageSize={pageSize}
      showSearch={showSearch}
    />
  );
}
